import React, { useState } from 'react';
import { Calendar, Users, Clock, MapPin, Info, ArrowRight } from 'lucide-react';
import Button from '../../components/ui/Button';
import ConfettiButton from '../../components/ui/ConfettiButton';
import { useSocial } from '../../context/SocialContext';
const MysteryLunch: React.FC = () => {
  const {
    mysteryLunchMatches,
    registerForMysteryLunch
  } = useSocial();
  const [isRegistering, setIsRegistering] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const handleRegister = async () => {
    setIsRegistering(true);
    try {
      await registerForMysteryLunch();
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } finally {
      setIsRegistering(false);
    }
  };
  return <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 md:pb-0" data-id="element-1101">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#E84118] to-[#c83616] rounded-lg p-6 md:p-8 text-white mb-6 md:mb-8 relative overflow-hidden" data-id="element-1102">
        <div className="absolute right-0 bottom-0 opacity-10" data-id="element-1103">
          <Users size={200} data-id="element-1104" />
        </div>
        <h1 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4" data-id="element-1105">
          Mystery Lunch
        </h1>
        <p className="text-base md:text-lg opacity-90 mb-4 md:mb-6 max-w-xl" data-id="element-1106">
          Connect with professionals over lunch every Friday. Meet new people,
          share ideas, and enjoy great food!
        </p>
        {showSuccess ? <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 inline-block animate-pulse" data-id="element-1107">
            <p className="font-bold" data-id="element-1108">🎉 Successfully registered!</p>
            <p className="text-sm mt-1" data-id="element-1109">
              You'll be notified of your match soon
            </p>
          </div> : <ConfettiButton onClick={handleRegister} disabled={isRegistering} className="transform hover:scale-105 transition-transform duration-200" data-id="element-1110">
            {isRegistering ? <span className="flex items-center" data-id="element-1111">
                <span className="animate-spin mr-2" data-id="element-1112">⏳</span> Registering...
              </span> : <span className="flex items-center" data-id="element-1113">
                Join Next Friday's Lunch{' '}
                <ArrowRight className="ml-2" size={16} data-id="element-1114" />
              </span>}
          </ConfettiButton>}
      </div>
      {/* Info Banner */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 md:mb-8 rounded-r-md transform hover:translate-x-1 transition-transform duration-200" data-id="element-1115">
        <div className="flex" data-id="element-1116">
          <Info className="text-blue-500 mr-3 flex-shrink-0 mt-0.5" size={20} data-id="element-1117" />
          <p className="text-sm text-blue-700" data-id="element-1118">
            Our Mystery Lunch program pairs you with professionals from
            different departments or companies for networking over lunch. A
            great way to expand your professional circle!
          </p>
        </div>
      </div>
      {/* How it works */}
      <div className="bg-white rounded-lg shadow-md p-5 md:p-6 mb-6 md:mb-8" data-id="element-1119">
        <h2 className="text-lg md:text-xl font-bold mb-5 md:mb-6" data-id="element-1120">
          How it works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6" data-id="element-1121">
          <div className="flex flex-col items-center text-center transform hover:scale-105 transition-transform duration-200 p-4 rounded-lg hover:bg-orange-50" data-id="element-1122">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-3 md:mb-4" data-id="element-1123">
              <Calendar className="text-[#E84118]" data-id="element-1124" />
            </div>
            <h3 className="font-medium mb-2" data-id="element-1125">Register</h3>
            <p className="text-gray-600 text-sm" data-id="element-1126">
              Sign up before Thursday midnight for Friday's lunch
            </p>
          </div>
          <div className="flex flex-col items-center text-center transform hover:scale-105 transition-transform duration-200 p-4 rounded-lg hover:bg-orange-50" data-id="element-1127">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-3 md:mb-4" data-id="element-1128">
              <Users className="text-[#E84118]" data-id="element-1129" />
            </div>
            <h3 className="font-medium mb-2" data-id="element-1130">Get Matched</h3>
            <p className="text-gray-600 text-sm" data-id="element-1131">
              We'll pair you with another professional from your area
            </p>
          </div>
          <div className="flex flex-col items-center text-center transform hover:scale-105 transition-transform duration-200 p-4 rounded-lg hover:bg-orange-50" data-id="element-1132">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-3 md:mb-4" data-id="element-1133">
              <MapPin className="text-[#E84118]" data-id="element-1134" />
            </div>
            <h3 className="font-medium mb-2" data-id="element-1135">Meet & Eat</h3>
            <p className="text-gray-600 text-sm" data-id="element-1136">
              Meet at the selected restaurant and enjoy your lunch
            </p>
          </div>
        </div>
      </div>
      {/* Upcoming/Past Matches */}
      <div className="bg-white rounded-lg shadow-md p-5 md:p-6" data-id="element-1137">
        <h2 className="text-lg md:text-xl font-bold mb-5 md:mb-6" data-id="element-1138">
          Your Lunch Matches
        </h2>
        {mysteryLunchMatches.length === 0 ? <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100" data-id="element-1139">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1140">
              <Users className="text-gray-400" size={24} data-id="element-1141" />
            </div>
            <p className="text-gray-500 mb-4" data-id="element-1142">
              You haven't participated in any Mystery Lunches yet.
            </p>
            <Button variant="outline" onClick={handleRegister} className="transform hover:scale-105 transition-transform duration-200" data-id="element-1143">
              Join Your First Mystery Lunch
            </Button>
          </div> : <div className="space-y-4" data-id="element-1144">
            {mysteryLunchMatches.map(match => <div key={match.id} className="border rounded-lg p-4 transform hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-id="element-1145">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-1146">
                  <div className="flex items-center mb-3 sm:mb-0" data-id="element-1147">
                    <div className="w-10 h-10 bg-gray-200 rounded-full mr-3 flex items-center justify-center" data-id="element-1148">
                      {match.partnerName.charAt(0)}
                    </div>
                    <div data-id="element-1149">
                      <h3 className="font-medium" data-id="element-1150">{match.partnerName}</h3>
                      <p className="text-sm text-gray-500" data-id="element-1151">
                        {match.date.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs sm:text-sm inline-flex items-center ${match.status === 'completed' ? 'bg-green-100 text-green-800' : match.status === 'confirmed' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1152">
                    {match.status.charAt(0).toUpperCase() + match.status.slice(1)}
                  </span>
                </div>
                <div className="mt-3 text-sm text-gray-600 grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-1153">
                  <div className="flex items-center" data-id="element-1154">
                    <MapPin size={16} className="mr-2 flex-shrink-0" data-id="element-1155" />
                    <span className="truncate" data-id="element-1156">{match.location}</span>
                  </div>
                  <div className="flex items-center" data-id="element-1157">
                    <Clock size={16} className="mr-2 flex-shrink-0" data-id="element-1158" />
                    12:30 PM - 1:30 PM
                  </div>
                </div>
              </div>)}
          </div>}
      </div>
    </div>;
};
export default MysteryLunch;